export type WheelVisualDirection = 'broadcast' | 'premium' | 'modern';

export interface WheelVisualTheme {
  id: WheelVisualDirection;
  studyLabel: string;
  cashColors: readonly string[];
  bankruptColor: string;
  loseTurnColor: string;
  freePlayColor: string;
  topDollarColor: string;
  darkInk: string;
  lightInk: string;
  dividerColor: string;
  faceRingColor: string;
  rimStops: readonly [string, string, string, string];
  rimEdge: string;
  rimHighlight: string;
  sidewall: string;
  sidewallEdge: string;
  hubStops: readonly [string, string, string];
  hubAccent: string;
  hubMark: string;
  pointerBody: number;
  pointerAccent: number;
  lightTint: string;
  ledColor: number;
  tilt: number;
  sidewallDepth: number;
  gradientStrength: number;
  showLeds: boolean;
  showShapes: boolean;
}

const THEMES: Record<WheelVisualDirection, WheelVisualTheme> = {
  broadcast: {
    id: 'broadcast',
    studyLabel: 'Broadcast Wheel Study',
    cashColors: [
      '#FFD43B',
      '#39C96B',
      '#27A8ED',
      '#8B6CF2',
      '#FF8A2C',
      '#F3549A',
      '#20C7BA',
      '#EE4E4E',
    ],
    bankruptColor: '#07090F',
    loseTurnColor: '#4C5266',
    freePlayColor: '#FFF3B0',
    topDollarColor: '#DA123E',
    darkInk: '#101522',
    lightInk: '#FFFDF4',
    dividerColor: 'rgba(255, 255, 255, 0.78)',
    faceRingColor: 'rgba(255, 255, 255, 0.22)',
    rimStops: ['#F9FCFF', '#A8C5DB', '#435C75', '#111A29'],
    rimEdge: '#07101F',
    rimHighlight: '#F8FDFF',
    sidewall: '#101D35',
    sidewallEdge: '#4A6B8C',
    hubStops: ['#F8FCFF', '#4E7EAE', '#07101F'],
    hubAccent: '#FFD43B',
    hubMark: '#07101F',
    pointerBody: 0xf4f7fb,
    pointerAccent: 0xffd43b,
    lightTint: 'rgba(208, 239, 255, 0.34)',
    ledColor: 0x71e5ff,
    tilt: 0.91,
    sidewallDepth: 0.052,
    gradientStrength: 0.42,
    showLeds: true,
    showShapes: false,
  },
  premium: {
    id: 'premium',
    studyLabel: 'Premium Physical Study',
    cashColors: [
      '#D7A92D',
      '#4D8B62',
      '#34749A',
      '#6C4D87',
      '#C66E2C',
      '#A84F70',
      '#457C77',
      '#A84137',
    ],
    bankruptColor: '#100A08',
    loseTurnColor: '#595258',
    freePlayColor: '#F1E0A8',
    topDollarColor: '#8F1830',
    darkInk: '#26180D',
    lightInk: '#FFF1D0',
    dividerColor: 'rgba(244, 207, 120, 0.72)',
    faceRingColor: 'rgba(255, 229, 166, 0.17)',
    rimStops: ['#FFF0B7', '#D5A844', '#8D5B1E', '#3B210B'],
    rimEdge: '#321807',
    rimHighlight: '#FFF1B7',
    sidewall: '#39200F',
    sidewallEdge: '#8C5D26',
    hubStops: ['#F8D986', '#6E3518', '#140806'],
    hubAccent: '#E8B94F',
    hubMark: '#2C1508',
    pointerBody: 0x3a1d0e,
    pointerAccent: 0xe7b84f,
    lightTint: 'rgba(255, 222, 153, 0.26)',
    ledColor: 0xffd777,
    tilt: 0.89,
    sidewallDepth: 0.07,
    gradientStrength: 0.5,
    showLeds: false,
    showShapes: true,
  },
  modern: {
    id: 'modern',
    studyLabel: 'Modern Accessible Study',
    cashColors: [
      '#F2C94C',
      '#56B4E9',
      '#009E73',
      '#CC79A7',
      '#E69F00',
      '#0072B2',
      '#D55E00',
      '#8B72C6',
    ],
    bankruptColor: '#141414',
    loseTurnColor: '#676B73',
    freePlayColor: '#FFF4C2',
    topDollarColor: '#B51D45',
    darkInk: '#151515',
    lightInk: '#FFFFFF',
    dividerColor: 'rgba(255, 255, 255, 0.94)',
    faceRingColor: 'rgba(255, 255, 255, 0.16)',
    rimStops: ['#FFFFFF', '#D8DCE2', '#969EAA', '#505863'],
    rimEdge: '#242A31',
    rimHighlight: '#FFFFFF',
    sidewall: '#3D454F',
    sidewallEdge: '#8E99A6',
    hubStops: ['#FFFFFF', '#CDD3DB', '#555E69'],
    hubAccent: '#151515',
    hubMark: '#FFFFFF',
    pointerBody: 0xffffff,
    pointerAccent: 0x151515,
    lightTint: 'rgba(255, 255, 255, 0.22)',
    ledColor: 0xffffff,
    tilt: 0.96,
    sidewallDepth: 0.026,
    gradientStrength: 0.16,
    showLeds: false,
    showShapes: true,
  },
};

/** Change only this selector when deriving a visual-study branch. */
export const WHEEL_VISUAL_DIRECTION: WheelVisualDirection = 'modern';

export const WHEEL_VISUAL = THEMES[WHEEL_VISUAL_DIRECTION];
